import type { ReactNode } from 'react';

export interface StatProps {
  /** Lower-case label shown small underneath the value. */
  label: ReactNode;
  /** Headline value — typically a number, count, or short string. */
  value: ReactNode;
  /** Optional accent color for the value. Use `success`/`warning`/
   * `danger` to draw attention to a quality metric. Defaults to the
   * standard text color. */
  accent?: 'default' | 'success' | 'warning' | 'danger' | 'info';
}

const ACCENT_CLASS: Record<NonNullable<StatProps['accent']>, string> = {
  default: 'text-gray-900 dark:text-gray-100',
  success: 'text-emerald-500',
  warning: 'text-amber-500',
  danger:  'text-red-500',
  info:    'text-blue-500',
};

/**
 * Single statistic cell. Always rendered inside a `<StatStrip>`.
 *
 * @example
 * <Stat label="Total invocations" value="18.4k" />
 * <Stat label="Avg success" value="98.7%" accent="success" />
 */
export function Stat({ label, value, accent = 'default' }: StatProps) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-3">
      <span className={`text-base font-semibold tabular-nums leading-tight ${ACCENT_CLASS[accent]}`}>
        {value}
      </span>
      <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
        {label}
      </span>
    </div>
  );
}

export interface StatStripProps {
  children: ReactNode;
}

/**
 * Compact horizontal strip of stats with hairline dividers between
 * cells. Sits at the top of pages like Tasks, Leaderboard, Profile —
 * the lightweight alternative to a full dashboard.
 *
 * @example
 * <StatStrip>
 *   <Stat label="进行中" value={3} accent="info" />
 *   <Stat label="总任务" value={42} />
 *   <Stat label="已结算" value="¥1,200" accent="success" />
 * </StatStrip>
 */
export function StatStrip({ children }: StatStripProps) {
  return (
    <div className="px-4 py-3 border-b border-gray-100 dark:border-white/[0.06]">
      <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-white/[0.06]">
        {children}
      </div>
    </div>
  );
}
